import React from 'react';
import { Checkbox, FormControlLabel, makeStyles } from '@material-ui/core';
import valueType from 'src/helpers/valueType';
import Modal from 'src/component/Modal/Modal';
import camelCaseToTitleCase from 'src/helpers/camelCaseToTitleCase';
import { CustomerProfile } from 'src/services/napi/customers/interfaces/fetchCustomersProfile.interface';
import { getAvailableKeys, updateDynamicObject } from './helpers';
import CButton from '../../../../../component/Button';
import { CStandardInput } from '../../../../../component/Input';
import { MultipleSelect } from '../../../../../component/Select';

const useStyles = makeStyles({
  form: {
    display: 'grid',
    gap: '20px',
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  }
});

interface Props {
  customerProfileDraft?: CustomerProfile;
  onUpdate: () => void;
  open: boolean;
  setDraftProfile: (data: CustomerProfile) => void;
  setOpen: (value: boolean) => void;
}

const ProfileEditModal: React.FC<Props> = ({
  open,
  setOpen,
  customerProfileDraft,
  setDraftProfile,
  onUpdate
}) => {
  const classes = useStyles();

  const restrictedData: string[] = [
    '_id',
    'userId',
    'project',
    'profilePictureId',
    'profilePictureUrl',
    'createdAt',
    'updatedAt',
    'coverPictureId',
    'coverPictureUrl',
    'followed',
    'followers',
    'items',
    'followings',
    'items'
  ];
  const multilineData: string[] = ['bio'];
  const specialCaseData: string[] = ['settings'];

  function handleChange(value: string[]) {
    if (!customerProfileDraft) return;

    const newInterests: any[] = customerProfileDraft.categories.filter(
      (interest1: any) =>
        value.filter((interest2: string) => interest1.name === interest2).length !== 0
    );
    const newInterestsIds: string[] = newInterests.map((interest) => {
      return interest._id;
    });
    setDraftProfile({
      ...customerProfileDraft,
      settings: {
        ...customerProfileDraft.settings,
        interests: newInterestsIds
      }
    });
  }

  function structureSpecialCaseData(key: string) {
    if (key === 'settings' && customerProfileDraft) {
      const currentInterestsIds: string[] = customerProfileDraft?.settings?.interests;
      const currentInterests: string[] = currentInterestsIds?.map((id: string) => {
        const category: any = customerProfileDraft?.categories?.filter(
          (category: any) => category._id === id
        )[0];
        const selectedInterest: string = category?.name;
        return selectedInterest;
      });
      const interests: any = customerProfileDraft?.categories || [];

      return (
        <div
          key={key}
          style={{
            transform: 'translate(0px, -3px)'
          }}
        >
          <p
            style={{
              color: '#9BA0AD',
              fontFamily: 'Poppins',
              fontSize: '12px',
              lineHeight: '100%',
              margin: '0px'
            }}
          >
            Interests
          </p>
          <MultipleSelect
            currentValue={(currentInterests ?? []) as string[]}
            handleChange={handleChange}
            selectOptions={interests?.map((interest: any) => interest?.name) || []}
            styles={{ marginTop: '8px' }}
            type="chip"
          />
        </div>
      );
    }
  }

  const displayModalDataRecursive = (
    data: any,
    passedKey: string | undefined,
    pathArr: string[] = []
  ): React.ReactNode | null => {
    const dataType = valueType(data);

    if (dataType === 'string' && passedKey) {
      const isInputMultiline = multilineData.includes(passedKey);

      return (
        <CStandardInput
          key={passedKey}
          defaultValue={data}
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor={`standard-customer-edit-${passedKey}`}
          id={`standard-customer-edit-${passedKey}`}
          label={camelCaseToTitleCase(passedKey)}
          multiline={isInputMultiline}
          onChange={(event: any) => {
            const updatedObject: CustomerProfile = updateDynamicObject(
              customerProfileDraft,
              pathArr,
              event.target.value
            );
            setDraftProfile(updatedObject);
          }}
          rows={isInputMultiline ? '3' : '1'}
          size="large"
        />
      );
    }
    if (dataType === 'object') {
      const availableKeys: string[] = getAvailableKeys(data, restrictedData);

      const reactElement: React.ReactNode = availableKeys.map((key: string) => {
        const objValue: any = data[key];
        const pathDuplicateArr: string[] = pathArr.slice();
        pathDuplicateArr.push(key);

        const isKeySpecialCase: boolean = specialCaseData.includes(key);
        if (isKeySpecialCase) {
          return structureSpecialCaseData(key);
        }

        return displayModalDataRecursive(objValue, key, pathDuplicateArr);
      });

      return reactElement;
    }

    if (dataType === 'boolean') {
      return (
        <FormControlLabel
          key={passedKey}
          className="tick"
          control={
            <Checkbox
              checked={data}
              color="primary"
              name="checked"
              onChange={(event: any) => {
                const updatedObject: CustomerProfile = updateDynamicObject(
                  customerProfileDraft,
                  pathArr,
                  !data
                );
                setDraftProfile(updatedObject);
              }}
            />
          }
          label={camelCaseToTitleCase(passedKey)}
          labelPlacement="top"
        />
      );
    }

    return null;
  };

  return (
    <Modal
      footerActions={
        <>
          <CButton
            htmlType="submit"
            onClick={(e: any) => {
              setOpen(false);
              e.preventDefault();
            }}
            size="large"
            type="secondary"
          >
            Cancel
          </CButton>
          <CButton htmlType="submit" onClick={onUpdate} size="large" type="primary">
            Save
          </CButton>
        </>
      }
      handleClose={() => setOpen(false)}
      isModalOpen={open}
      scrollType="paper"
      styles={{
        // @ts-ignore
        '& .MuiDialogContent-root': {
          paddingBottom: '24px'
        },
        maxWidth: '770px',
        width: customerProfileDraft ? '770px' : '385px'
      }}
      titleContent={<span className="modal-title">Edit User</span>}
    >
      <form className={classes.form} id="customer-edit-form">
        {displayModalDataRecursive(customerProfileDraft, undefined, [])}
      </form>
    </Modal>
  );
};

export default ProfileEditModal;
